import type { FixMessage, FixMessageTag, ParseResult } from "../../domain";
import type {
  FixMessageFields,
  SerializedFixMessage,
} from "./worker/workerProtocol";
import { RUNTIME_LIMITS } from "./worker/workerProtocol";
import {
  decodeStructure,
  type StructureNode,
} from "../../utils/structure-decoder";
import {
  createDataDictionaryFieldsIndex,
  type DataDictionary,
} from "../../utils/data-dictionary";

/**
 * Default snippet shown to new users. Reset restores exactly this. It must run
 * cleanly against any validly parsed FIX session and produce a DataFrame. The
 * header documents the runtime environment (preloaded modules, the exposed
 * data, limits); the imports are redundant (pd/np/plt are preloaded) but make
 * the snippet self-documenting and portable.
 */
export const STARTER_CODE = `# FIX Code Lens — runs entirely in your browser (Pyodide). Nothing is uploaded.
#
# Your parsed session is exposed two ways:
#   messages      -> list[dict], one dict per message, keyed by FIX field name
#                    (e.g. msg["MsgType"], msg["ClOrdID"]). Repeating groups are
#                    nested: msg["NoPartyIDs"] -> list of per-entry dicts.
#   df_messages   -> pandas DataFrame of the same data; leading idx / timestamp /
#                    raw columns plus one column per FIX field seen.
#   display(value)-> render a DataFrame, chart, text, or value as the output.
#
# Preloaded: pandas as pd, numpy as np, matplotlib.pyplot as plt.
# Limits: <=10,000 messages in, <=1,000 rows rendered, 64 KB stdout, 5s runtime.

import pandas as pd
import numpy as np
import matplotlib.pyplot as plt

summary = (
    df_messages.groupby("MsgType")
      .size()
      .reset_index(name="count")
      .sort_values("count", ascending=False)
)

display(summary)
`;

/** True when the editor holds nothing executable (empty / whitespace only). */
export const isBlankCode = (code: string): boolean => code.trim().length === 0;

/**
 * Builds a `(nodeIndex) => fieldName` resolver. A structure node's `startIndex`
 * points back into the original tag list, whose entries already carry a
 * resolved `tagDescription`. Precedence: custom data-dictionary label →
 * parser's `tagDescription` → the raw tag number as a string fallback.
 */
const makeFieldNameResolver = (
  tags: FixMessageTag[],
  fixDictionary?: DataDictionary,
): ((nodeIndex: number) => string) => {
  const tagToLabel = fixDictionary?.fieldsIndex?.tagToLabel;
  return (nodeIndex: number): string => {
    const t = tags[nodeIndex];
    if (!t) return String(nodeIndex);
    const custom = tagToLabel?.get(t.tag);
    if (custom) return custom;
    if (t.tagDescription) return t.tagDescription;
    return String(t.tag);
  };
};

/**
 * Flat name-keyed dict over every tag, in order. Used as a fallback when the
 * decoder can't recognise the message's version (no/unknown BeginString), since
 * its header-less path drops the leading header tags — this keeps every tag
 * reachable, just without group nesting (which needs a known version anyway).
 */
const flatFields = (
  tags: FixMessageTag[],
  name: (nodeIndex: number) => string,
): FixMessageFields => {
  const out: FixMessageFields = {};
  tags.forEach((t, i) => {
    out[name(i)] = t.value;
  });
  return out;
};

/**
 * Walks the decoded structure nodes into a FIX-name-keyed dict. Plain fields
 * map name -> value; non-repeating components are inlined into the parent dict;
 * repeating groups become `countFieldName -> [entryDict, …]` (recursively, so
 * nested groups nest). Duplicate names at one level: last wins.
 */
const nodesToFields = (
  nodes: StructureNode[],
  name: (nodeIndex: number) => string,
  out: FixMessageFields,
): FixMessageFields => {
  for (const node of nodes) {
    switch (node.kind) {
      case "Field":
        out[name(node.startIndex)] = node.value;
        break;
      case "XmlData":
        out[name(node.startIndex)] = node.data;
        break;
      case "Component":
        nodesToFields(node.children, name, out);
        break;
      case "Group":
        out[name(node.startIndex)] = node.entries.map((entry) =>
          nodesToFields(entry.children, name, {}),
        );
        break;
    }
  }
  return out;
};

/**
 * Structures parsed FIX messages into the wire form sent to the worker. Each
 * message becomes a nested, FIX-name-keyed `fields` dict (repeating groups
 * nested as lists) via the shared `decodeStructure`, plus idx/timestamp/raw
 * metadata. Caps the input at the MVP limit; the cap is reported so the UI can
 * warn. Missing data is tolerated (never throws) so malformed messages still
 * ride along.
 */
export const serializeMessagesForWorker = (
  fixMessages: ParseResult<FixMessage>[],
  fixDictionary?: DataDictionary,
): { messages: SerializedFixMessage[]; truncated: boolean; total: number } => {
  // Ensure the custom-dictionary field-name index exists (mirrors what
  // decodeStructure does) so labels resolve from a user-supplied dictionary.
  if (fixDictionary && !fixDictionary.fieldsIndex) {
    try {
      fixDictionary.fieldsIndex = createDataDictionaryFieldsIndex(
        fixDictionary.fields || [],
      );
    } catch {
      // Fall back to built-in names only.
    }
  }

  const total = fixMessages.length;
  const capped = fixMessages.slice(0, RUNTIME_LIMITS.maxInputMessages);
  const messages: SerializedFixMessage[] = capped.map((result, idx) => {
    const data = result.data;
    const tags = data?.tags ?? [];
    const name = makeFieldNameResolver(tags, fixDictionary);
    const structure = decodeStructure(tags, fixDictionary);
    // When the version is unknown the decoder emits no header (and skips the
    // leading header tags); fall back to a flat dict so no tag is lost.
    const fields =
      structure.header.length === 0 && tags.length > 0
        ? flatFields(tags, name)
        : nodesToFields(
            [...structure.header, ...structure.body, ...structure.trailer],
            name,
            {},
          );

    // SendingTime is tag 52; fall back to the message `time` the parser
    // surfaces so a row always has its best-known timestamp.
    let timestamp: string | null = null;
    for (const t of tags) {
      if (t.tag === 52) {
        timestamp = t.value;
        break;
      }
    }
    if (timestamp === null && data?.time) timestamp = data.time;

    return {
      idx,
      timestamp,
      raw: data?.message ?? result.source ?? "",
      fields,
    };
  });
  return {
    messages,
    truncated: total > RUNTIME_LIMITS.maxInputMessages,
    total,
  };
};

export { RUNTIME_LIMITS };
